export const DEPLOY_WARNING_TAG = 'deploy-warning';
const STORAGE_KEY = 'deploy_warning_at';
// The banner gives up this long after the deploy time; by then the new version is up.
export const STALE_AFTER_MS = 5 * 60 * 1000;

/** The deploy moment a push announces, in ms; null for any other push. */
export function deployAtFrom(data: unknown, now: number): number | null {
  const d = data as { type?: unknown; deployAt?: unknown; minutes?: unknown } | null | undefined;
  if (!d || d.type !== 'deploy_warning') return null;
  const at = typeof d.deployAt === 'string' ? Date.parse(d.deployAt) : NaN;
  if (!Number.isNaN(at)) return at;
  // A push from a backend that predates deployAt: count from when it arrived.
  const minutes = Number(d.minutes);
  return d.minutes !== undefined && Number.isFinite(minutes) ? now + minutes * 60_000 : null;
}

/** A push saying the scheduled deploy was cancelled. */
export function isDeployCancelled(data: unknown): boolean {
  const d = data as { type?: unknown; cancelled?: unknown } | null | undefined;
  return !!d && d.type === 'deploy_warning' && d.cancelled === 'true';
}

export const isStale = (deployAt: number, now: number): boolean => now > deployAt + STALE_AFTER_MS;

/** 4:05 for 245 s; never negative. */
export function formatRemaining(ms: number): string {
  const seconds = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

/** Nearest whole minute, so 2:04 reads "2 minutes"; never 0 before the deploy time. */
export const minutesLeft = (ms: number): number => (ms <= 0 ? 0 : Math.max(1, Math.round(ms / 60_000)));

export function readStoredDeployAt(now: number): number | null {
  try {
    const value = Number(localStorage.getItem(STORAGE_KEY));
    return value && !isStale(value, now) ? value : null;
  } catch {
    return null;
  }
}

export function storeDeployAt(deployAt: number | null): void {
  try {
    if (deployAt === null) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, String(deployAt));
  } catch {
    // Storage blocked: the banner still works for this page view.
  }
}

async function pushRegistration(): Promise<ServiceWorkerRegistration | undefined> {
  if (!('serviceWorker' in navigator) || typeof Notification === 'undefined') return undefined;
  if (Notification.permission !== 'granted') return undefined;
  return navigator.serviceWorker.getRegistration('/firebase-push-notification-scope');
}

/** Replace the system notification's text in place (same tag, no new alert). */
export async function updateSystemNotification(title: string, body: string): Promise<void> {
  try {
    const registration = await pushRegistration();
    await registration?.showNotification(title, {
      body,
      icon: `${window.location.origin}/media/logos/homecarelogo_sm.png`,
      tag: DEPLOY_WARNING_TAG,
      data: { data: { type: 'deploy_warning' } },
    });
  } catch {
    // Best effort: the in-app banner is the main signal.
  }
}

export async function closeSystemNotification(): Promise<void> {
  try {
    const registration = await pushRegistration();
    (await registration?.getNotifications({ tag: DEPLOY_WARNING_TAG }))?.forEach((n) => n.close());
  } catch {
    // Nothing to close.
  }
}
